import {
  Alert,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import {
  useActivityTypesList,
  useClock,
  useOpenTaskDrawer,
  useOrgsList,
  useRestartTimer,
  useStartTimer,
  useStopTimer,
} from "../hooks";
import { createPortal } from "react-dom";
import { isDate, useLocation } from "../../lib";
import { TimerButton } from "../buttons";
import {
  TimerIcon,
  ChevronDown,
  RestartIcon,
  StopIcon,
  StopwatchSlashIcon,
  CancelIcon,
  PlayCircleIcon,
} from "../icons";
import { EllipsisTypographyStyled } from "../styled";
import { differenceInSeconds } from "date-fns";
import { useEffect, useCallback, useMemo, memo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { matchPath, useNavigate } from "react-router-dom";
import { MainPages } from "../../pages";
import { authSelectors, timerSelectors, timerActions } from "../../state";
import {
  useUpdateTimerMutation,
  useGetMyActiveTimerQuery,
  useCancelTimersMutation,
} from "../../state/rtk-query/state/timer";
import { useMobile } from "../../themes";
import { SwitchingMessage } from "./SwitchingMessage";

const TIMER_SWITCHING_COUNTDOWN = 5;

interface Props {
  setBgColor: (color: string) => void;
}

export const Clock = memo(({ setBgColor }: Props) => {
  const nav = useNavigate();
  const isMobile = useMobile();
  const { palette } = useTheme();
  const dispatch = useDispatch();
  const { activityTypes, getActivityTypes } = useActivityTypesList();
  const isInternal = useSelector(authSelectors.isInternal);
  const isCW = useSelector(authSelectors.isCustomWorkUser);
  const { getOrg } = useOrgsList(!isInternal);
  const [cancelTimers] = useCancelTimersMutation();
  const openTaskDrawer = useOpenTaskDrawer();
  const {
    id: timerId,
    orgId: timerOrgId,
    state: timerState,
    customWork,
    initializing,
  } = useSelector(timerSelectors.currentTimer);
  const inferredOrgId = useSelector(timerSelectors.inferredOrgId);
  const timerTransitioning = useSelector(timerSelectors.timerIsTransitioning);
  const cancelledSwitch = useSelector(timerSelectors.cancelledSwitch);
  const [updateTimer] = useUpdateTimerMutation();
  const restart = useRestartTimer();
  const startTimer = useStartTimer();
  const stopTimer = useStopTimer();
  const inferredOrg = getOrg(Number(inferredOrgId));
  const { pathname } = useLocation();
  const { data } = useGetMyActiveTimerQuery(undefined, {
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
    refetchOnReconnect: true,
    skip: !isInternal,
  });

  const isRunning = useMemo(
    () => ["active", "switching", "stopping"].includes(timerState) || false,
    [timerState],
  );

  const { time, setTime } = useClock({
    direction: "up",
    start: isRunning,
  });

  const cancelCurrentTimer = async () => {
    if (timerId) {
      dispatch(timerActions.stopTimer(true));
      await cancelTimers();
    } else {
      throw new Error("Timer ID is missing");
    }
  };
  const restartTimer = useCallback(async () => {
    if (restart) {
      await restart();
      setTime(0);
    }
  }, [restart, setTime]);

  const {
    time: switchingCount,
    setTime: setSwitchingCount,
    stopClock: clearSwitchTimer,
  } = useClock({
    direction: "down",
    start: timerTransitioning && !cancelledSwitch,
    defaultCount: TIMER_SWITCHING_COUNTDOWN,
  });

  useEffect(() => {
    let bgColor;
    if (timerTransitioning) {
      bgColor = palette.warning[600];
    }
    if (cancelledSwitch) {
      bgColor = palette.orange[600];
    }
    if (!isRunning) {
      bgColor = palette.error[700];
    }
    if (isRunning && timerOrgId === inferredOrgId) {
      bgColor = palette.primary.main;
    }

    if (bgColor) {
      setBgColor(bgColor);
    }
  }, [
    timerState,
    setBgColor,
    cancelledSwitch,
    isRunning,
    timerOrgId,
    inferredOrgId,
  ]);

  /** Sync DB with Frontend Start*/

  useEffect(() => {
    if (data && data?.start_time && isDate(data?.start_time)) {
      setTime((old) => {
        const timeDif =
          data?.start_time &&
          differenceInSeconds(new Date(), new Date(data?.start_time));
        // if the difference between the actual time and the state time is more than 5 seconds update the time
        if (!data?.start_time || (timeDif && Math.abs(timeDif - time) <= 5)) {
          return old + 1;
        }

        return differenceInSeconds(new Date(), new Date(data?.start_time)) + 1;
      });
    }
  }, [data?.start_time]);
  /** Sync DB with Frontend End */

  useEffect(() => {
    if (!timerId) return;

    (async () => {
      if (
        switchingCount === 0 &&
        timerState === "switching" &&
        inferredOrgId &&
        cancelledSwitch === false
      ) {
        console.log("About to restart timer => switchingCount", switchingCount);
        await restartTimer();
        console.log("Done restarting timer");
        setSwitchingCount(TIMER_SWITCHING_COUNTDOWN);
      } else if (switchingCount === 0 && timerState === "stopping") {
        console.log("Need to stop now");
        const orgPath = matchPath(MainPages(true).organization.path, pathname);
        const isOrgPage = !!orgPath && orgPath.params.id;
        await stopTimer(!!isOrgPage);
        setSwitchingCount(TIMER_SWITCHING_COUNTDOWN);
      }
    })();
  }, [timerId, switchingCount, timerState, inferredOrgId]);
  useEffect(() => {
    //If somehow got below 0 set to 0
    if (switchingCount < 0) {
      setSwitchingCount(0);
    }
  }, [switchingCount]);
  useEffect(() => {
    //Do this if stopped in middle
    if (timerTransitioning === false) {
      setSwitchingCount(TIMER_SWITCHING_COUNTDOWN);
      if (cancelledSwitch === true) {
        //setTimeout(() => {
        //Reset cancelledSwitch so that it should trigger timer countdown once everything settles
        //   dispatch(timerActions.setTimerCancelledSwitch(false));
        // }, 2000);
      }
    }
  }, [timerTransitioning, cancelledSwitch]);

  const stayOnCurrentOrg = useCallback(() => {
    // console.log("stayOnCurrentOrg", { timerOrgId });
    // if (!timerOrgId) return;
    clearSwitchTimer();
    dispatch(timerActions.setTimerCancelledSwitchAction());
    setSwitchingCount(0);
  }, [clearSwitchTimer, dispatch, setSwitchingCount]);

  const switchToNextOrg = useCallback(async () => {
    await restartTimer();
    clearSwitchTimer();
    setSwitchingCount(TIMER_SWITCHING_COUNTDOWN);
  }, [clearSwitchTimer, restartTimer, setSwitchingCount]);
  /** Handle "switch" state end */

  const hours = `${Math.floor(time / 3600)}`.padStart(2, "0");
  const minutes = `${Math.floor((time % 3600) / 60)}`.padStart(2, "0");
  const seconds = `${Math.floor(time % 60)}`.padStart(2, "0");

  const displayCW =
    isCW &&
    timerState === "active" &&
    customWork?.taskCode &&
    customWork?.taskId &&
    customWork.taskTitle;

  const handleChange = useCallback(
    async (event: SelectChangeEvent<unknown>) => {
      console.log("UPDATE TIMER - handleChange", {
        id: timerId,
        body: {
          end_time: null,
          activity_type_id: Number(event.target.value),
          org_id: timerOrgId,
        },
      });
      if (
        timerId &&
        timerOrgId &&
        getActivityTypes(Number(event.target.value ?? 0)) &&
        initializing !== true
      ) {
        await updateTimer({
          id: timerId,
          body: {
            end_time: null,
            activity_type_id: Number(event.target.value),
            org_id: timerOrgId,
          },
        });
      }
    },
    [timerOrgId, timerId, getActivityTypes, initializing, updateTimer],
  );

  const timeEntriesPage = useMemo(() => MainPages(true).timer.path, []);

  const navToTimerEntries = useCallback(
    () => (isMobile ? nav(timeEntriesPage) : null),
    [isMobile, nav, timeEntriesPage],
  );

  const openTask = useCallback(() => {
    if (displayCW) {
      openTaskDrawer(customWork.taskId);
    }
  }, [displayCW, openTaskDrawer, customWork?.taskId]);

  const dataOrg = getOrg(data?.org_id);
  const stopAndEditCurrentTimer = async () => {
    await stopTimer();
    window.open(
      `${MainPages(true).timer.path}?timerId=${timerId}&open=true&mode=edit`,
    );
  };

  const handleStartTimer = useCallback(() => {
    if (!startTimer) {
      throw new Error("startTimer not ready");
    }
    if (!inferredOrgId) {
      throw new Error("Missing inferredOrgId");
    }
    startTimer({ org_id: Number(inferredOrgId), manualStarted: true });
  }, [inferredOrgId, startTimer]);

  if (!isInternal) return null;

  return (
    <>
      <Stack
        direction={"row"}
        alignItems={isMobile ? "baseline" : "center"}
        gap={1}
        justifyContent={isMobile ? "space-between" : "flex-start"}
        width={isMobile ? "100%" : "auto"}
      >
        <Stack
          direction={"row"}
          alignItems={isMobile ? "flex-start" : "center"}
          onClick={navToTimerEntries}
          width={"100%"}
          maxWidth={isMobile ? "60%" : "100%"}
        >
          <TimerIcon size={isMobile ? 18 : 20} style={{ flexShrink: 0 }} />
          <Typography
            fontSize={16}
            fontWeight={600}
            pl={1}
            width={"100%"}
            whiteSpace={"nowrap"}
            sx={{
              textOverflow: isMobile ? "ellipsis" : "-moz-initial",
              overflow: isMobile ? "hidden" : "initial",
              whiteSpace: "nowrap",
            }}
          >
            {data?.org_id === -1
              ? "Admire"
              : dataOrg?.name || (inferredOrg && inferredOrg?.name) || ""}
          </Typography>
          <Typography fontSize={16} fontWeight={600} pl={1} width={"85px"}>
            {isRunning ? `${hours}:${minutes}:${seconds}` : "00:00:00"}
          </Typography>
        </Stack>

        <Stack
          direction={isMobile ? "column" : "row"}
          alignItems={isMobile ? "flex-start" : "center"}
        >
          {isRunning ? (
            <>
              {!isMobile ? (
                <Stack
                  direction={"row"}
                  alignItems={"center"}
                  mx={isMobile ? 0 : 2}
                >
                  <Select
                    label="activity"
                    value={data?.activity_type_id ?? ""}
                    onChange={handleChange}
                    autoWidth
                    IconComponent={ChevronDown}
                    disableUnderline
                    variant="standard"
                    defaultValue={"activity"}
                    displayEmpty
                    renderValue={(v: any) => {
                      return (
                        <Typography
                          pl={1}
                          pt={0.25}
                          fontWeight={600}
                          sx={{ display: "flex", alignItems: "baseline" }}
                        >
                          {typeof v === "string" && v?.trim() === ""
                            ? "Select an activity" // what's default
                            : getActivityTypes(Number(v ?? 0))?.name}
                        </Typography>
                      );
                    }}
                    sx={{
                      "& > svg": {
                        top: ".5em",
                        color: "#FFF !important",
                      },
                      "& .MuiSelect-select": {
                        backgroundColor: "transparent !important",
                      },
                      "& .MuiInputBase-input": {
                        fontWeight: 600,
                        fontSize: 16,
                      },
                      "& .MuiSelect-select[aria-expanded='true']": {
                        backgroundColor: "rgba(255,255,255,0.12) !important",
                        borderRadius: 2,
                      },
                      color: "#FFF",
                    }}
                  >
                    {activityTypes?.map((at) => {
                      return (
                        <MenuItem
                          key={at.id}
                          value={at.id}
                          sx={{ fontWeight: 600, fontSize: 16 }}
                        >
                          {at.name}
                        </MenuItem>
                      );
                    })}
                  </Select>
                  {displayCW ? (
                    <EllipsisTypographyStyled
                      onClick={openTask}
                      width={"100%"}
                      fontWeight={600}
                      fontSize={16}
                      ml={2}
                      maxWidth={"400px"}
                      sx={{ cursor: "pointer" }}
                    >{`[${customWork.taskCode}] - ${customWork.taskTitle}`}</EllipsisTypographyStyled>
                  ) : null}
                </Stack>
              ) : null}
              <Stack
                direction={"row"}
                alignItems={"center"}
                gap={isMobile ? 0 : 2}
                mr={2}
                width={isMobile ? "100%" : "auto"}
                justifyContent={isMobile ? "flex-end" : "flex-start"}
              >
                <TimerButton
                  onClick={restartTimer}
                  Icon={RestartIcon}
                  text="Restart"
                  disabled={timerTransitioning}
                />
                <TimerButton
                  onClick={stopTimer}
                  Icon={StopIcon}
                  text="Stop"
                  disabled={timerTransitioning}
                />
                <TimerButton
                  Icon={StopwatchSlashIcon}
                  onClick={stopAndEditCurrentTimer}
                  sx={{ minWidth: "fit-content" }}
                  text="Stop and edit"
                  disabled={timerTransitioning}
                />
                <TimerButton
                  onClick={cancelCurrentTimer}
                  Icon={CancelIcon}
                  text="Cancel"
                  disabled={timerTransitioning}
                />
              </Stack>
            </>
          ) : (
            inferredOrgId && (
              <TimerButton
                onClick={handleStartTimer}
                Icon={PlayCircleIcon}
                text="Start"
                disabled={timerTransitioning}
              />
            )
          )}
        </Stack>
      </Stack>
      {!isMobile ? (
        <Stack
          direction={isMobile ? "column" : "row"}
          alignItems={isMobile ? "flex-start" : "center"}
          // on dashboard we hide start button
          justifyContent={timerTransitioning ? "space-between" : "flex-end"}
          gap={2}
          mr={2}
          width={isMobile ? "100%" : "40%"}
        >
          {timerTransitioning && (
            <SwitchingMessage
              switchingCount={switchingCount}
              stayOnCurrentOrg={stayOnCurrentOrg}
              switchToNextOrg={switchToNextOrg}
            />
          )}
        </Stack>
      ) : null}
      {isMobile && timerTransitioning
        ? createPortal(
            <Alert
              severity={"warning"}
              sx={(theme) => ({
                position: "absolute",
                bottom: 12,
                borderRadius: "6px",
                left: 0,
                right: 0,
                margin: "auto",
                lineHeight: "1.25em",
                my: 2,
                zIndex: theme.zIndex.drawer + Number(timerId ? 3 : 1), // on top of the drawer if there is a timer running
                width: "90%",
                backgroundColor: palette.warning[600],
                color: "#FFF",
                fontWeight: 600,
                "& > .MuiAlert-icon": {
                  color: "#FFF",
                },
              })}
              action={
                <Stack direction={"row"} alignItems={"center"} height={"100%"}>
                  <TimerButton onClick={stayOnCurrentOrg} text="Stay" />
                </Stack>
              }
            >
              Switching to {inferredOrg?.name} in {switchingCount}
            </Alert>,
            document.body,
          )
        : null}
    </>
  );
});
